import React, { useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useAuth } from "../../context/studentAuthContext";

import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function NewJournalEntry() {
  const navigate = useNavigate();
  const { student } = useAuth();

  const [formData, setFormData] = useState({
    title: "",
    instructor: "",
    entry: "",
  });

  const [loading, setLoading] = useState(false);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/journal");
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error("Session title is required");
      return;
    }

    if (!formData.entry.trim()) {
      toast.error("Journal entry cannot be empty");
      return;
    }

    if (!student?.id) {
      toast.error("User not logged in");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${BASE_URL}/api/students/save-journal`,
        {
          studentId: student.id,
          title: formData.title,
          instructor: formData.instructor,
          entry: formData.entry,
        }
      );
      if (response.data.success) {
        toast.success("Journal entry saved successfully with AI Analysis!");
        setFormData({ title: "", instructor: "", entry: "" });
        
        setTimeout(() => {
          navigate("/journal");
        }, 1500);
      }
    } catch (err) {
      console.error("Failed to save journal:", err);
      toast.error(err.response?.data?.message || "Failed to save journal entry");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen mt-20 bg-gray-50 flex flex-col">
      <Navbar />

      <div className="flex flex-col items-center justify-center px-4 py-10 w-full flex-1">

        <ToastContainer position="top-right" autoClose={3000} />

        <div className="w-full max-w-2xl bg-white p-6 sm:p-8 rounded-xl shadow-sm">

          {/* Back Button */}
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-blue-600 mb-6"
          >
            <ArrowLeft size={18} />
            Back
          </button>

          {/* Title */}
          <h1 className="text-xl font-semibold text-gray-800 mb-1">
            New Journal Entry
          </h1>

          <p className="text-sm text-gray-400 mb-6">
            Describe what you learned today. Write reflections, ideas, and what
            stood out to you.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">

            {/* Session Title */}
            <div>
              <label className="block text-xs font-semibold text-gray-900 mb-1">
                Session Title
              </label>

              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter session title"
                className="w-full border-b border-gray-300 text-sm py-2 focus:outline-none focus:border-blue-600 placeholder-gray-300"
              />
            </div>

            {/* Instructor */}
            <div>
              <label className="block text-xs font-semibold text-gray-900 mb-1">
                Instructor
              </label>

              <input
                type="text"
                name="instructor"
                value={formData.instructor}
                onChange={handleChange}
                placeholder="Instructor name"
                className="w-full border-b border-gray-300 text-sm py-2 focus:outline-none focus:border-blue-600 placeholder-gray-300"
              />
            </div>

            {/* Journal Entry */}
            <div>
              <label className="block text-xs font-semibold text-gray-900 mb-1">
                Journal Entry
              </label>

              <textarea
                name="entry"
                value={formData.entry}
                onChange={handleChange}
                rows="5"
                placeholder="Write your journal entry here..."
                className="w-full border-b border-gray-300 text-sm py-2 resize-none focus:outline-none focus:border-blue-600 placeholder-gray-300"
              />
            </div>

            <p className="text-[11px] text-gray-400">
              You can edit this entry anytime.
            </p>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full flex justify-center items-center gap-2 text-sm font-semibold py-3 rounded transition ${
                loading
                  ? "bg-blue-300 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
              }`}
            >
              {loading ? "Saving..." : "Save Entry"}
            </button>

          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
}